/*
 * Feature: visible status and retry UI for chat frontend draft handoffs.
 * Implementation notes: never auto-opens on mount; external retries occur only from an explicit user click.
 * Recent changes: adds popup-blocked Gmail and Calendar retry controls without exposing draft payloads.
 */

import { useState, type FC } from 'react';
import { ArrowTopRightOnSquareIcon, CheckCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import type { ChatMessage } from '../../models/ChatMessage';
import { Button } from '../primitives';

interface ClientActionMessageProps {
  message: ChatMessage;
}

const ClientActionMessage: FC<ClientActionMessageProps> = ({ message }) => {
  const [copied, setCopied] = useState(false);
  const action = message.eventData?.details?.action;
  const actionUrl = message.eventData?.details?.actionUrl;
  const googleTaskClipboardText = message.eventData?.details?.googleTaskClipboardText;
  const automaticOpenStatus = message.eventData?.details?.automaticOpenStatus;
  const isBlockedExternalAction = (
    (action === 'open_email_draft' || action === 'open_meeting_draft' || action === 'open_google_task')
    && automaticOpenStatus === 'blocked'
    && typeof actionUrl === 'string'
  );

  const handleRetry = () => {
    if (!isBlockedExternalAction) {
      return;
    }
    let openedWindow: Window | null;
    try {
      openedWindow = window.open(actionUrl, '_blank');
    } catch {
      return;
    }
    if (openedWindow) {
      try {
        openedWindow.opener = null;
      } catch {
        // Cross-origin browser restrictions do not change whether the draft opened.
      }
    }
  };

  const handleCopyGoogleTask = async () => {
    if (action !== 'open_google_task' || typeof googleTaskClipboardText !== 'string') {
      return;
    }
    await navigator.clipboard.writeText(googleTaskClipboardText);
    setCopied(true);
  };

  const retryLabel = action === 'open_email_draft'
    ? 'Open Gmail'
    : action === 'open_google_task'
      ? 'Open Google Tasks'
      : 'Open Calendar';
  const isGoogleTask = action === 'open_google_task' && typeof googleTaskClipboardText === 'string';

  return (
    <div className="flex justify-start">
      <div className="max-w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{message.content}</span>
        </div>
        {isGoogleTask && (
          <pre className="mt-2 whitespace-pre-wrap rounded bg-white/70 p-2 text-xs text-gray-800 dark:bg-gray-900/50 dark:text-gray-100">
            {googleTaskClipboardText}
          </pre>
        )}
        {(isBlockedExternalAction || isGoogleTask) && (
          <div className="mt-2 flex flex-wrap gap-2">
            {isBlockedExternalAction && (
              <Button type="button" size="sm" variant="secondary" onClick={handleRetry}>
                <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                {retryLabel}
              </Button>
            )}
            {isGoogleTask && (
              <Button type="button" size="sm" variant="secondary" onClick={() => void handleCopyGoogleTask()}>
                <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                {copied ? 'Copied' : 'Copy task details'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientActionMessage;
